<script setup lang="ts">
import {
  ref,
  onMounted,
  onUnmounted,
  toRefs,
  watch,
  type StyleValue,
} from "vue";

interface Props {
  modelValue: string;
  searchText: string;
  label: string;
  fieldWidth?: number;
  groupId: string;
  customStyle: Partial<StyleValue>;
}

const props = defineProps<Props>();

const emit = defineEmits(["close", "updateInternal"]);

const { groupId, modelValue, searchText } = toRefs(props);
const slotRef = ref();
const listRef = ref();
// The position class to open the list through correct position
const positionClass = ref("");
const LIST_HEIGHT = 380;
// 58 (input) + 4 (margins)
const INPUT_HEIGHT = 62;

const CLOSE_KEYS = ["Tab", "Escape"];
const DIRECTION_KEYS = ["ArrowUp", "ArrowDown"];

const keyListener = (event: KeyboardEvent) => {
  // We should work on only the current dropdown group
  const currentGroup = `dropdown-group-${groupId.value}`;

  // Select the parent dropdown button element after item selection
  if (event.key === "Enter") {
    const groupButton = document.getElementsByClassName(
      `dropdown-${groupId.value}`,
    );
    if (groupButton.length === 1) {
      const button = groupButton[0];
      const timeout = setTimeout(() => {
        (button as HTMLButtonElement).focus();
        clearTimeout(timeout);
      }, 1);
    }
  }

  // We can close the dropdown list in this keys
  if (CLOSE_KEYS.includes(event.key)) {
    emit("close");
    return;
  }

  // If there is a special keys like up and down
  if (DIRECTION_KEYS.includes(event.key)) {
    // Don't allow the keys because it can trigger scrolling. We don't want that
    event.preventDefault();

    // Finding all focusable items
    const elements = document.getElementsByClassName("dropdown-focusable-item");

    // We filter them by the group and the disability value.
    const filtered = [];
    for (let index = 0; index < elements.length; index++) {
      if (
        elements[index]?.className.includes(currentGroup) &&
        (elements[index] as HTMLInputElement).disabled === false
      ) {
        filtered.push(elements[index]);
      }
    }

    // Getting the current index if there is any
    const currentIndex = filtered.findIndex(
      (element) => element === document.activeElement,
    );

    if (currentIndex === -1) {
      // There is not any active item, let's select the first one or the last one
      const newIndex = event.key === "ArrowDown" ? 0 : filtered.length - 1;
      (filtered.at(newIndex) as HTMLButtonElement)?.focus();
    } else {
      // If there is not any "NEXT" item on the list, we can jump to the start
      if (event.key === "ArrowDown" && currentIndex === filtered.length - 1) {
        (filtered.at(0) as HTMLButtonElement)?.focus();
      } else {
        // Let's discover the direction (UP or DOWN)
        const newIndex =
          event.key === "ArrowDown" ? currentIndex + 1 : currentIndex - 1;
        // Let's move to that direction
        (filtered.at(newIndex) as HTMLButtonElement)?.focus();
      }
    }
  }
};

const setScrollPosition = (value: string) => {
  // If user is still searching, we wait until they write a full hour.
  if (slotRef.value && value.length === 5) {
    // Find all elements with the `time-content` class inside `slotRef`
    const timeContentElements: HTMLDivElement[] =
      slotRef.value.querySelectorAll(".time-content");

    const toNumericHours = (value: string): number => {
      return parseInt(value.replace(":", ""));
    };

    const currentSelection = toNumericHours(value);

    for (const index in timeContentElements) {
      const element = timeContentElements[index];
      if (!element?.innerHTML) {
        continue;
      }

      const value = toNumericHours(element.innerHTML);

      // This is the closest time
      if (value >= currentSelection) {
        const elementHeight = (element.parentElement?.clientHeight || 55) - 1;
        listRef.value.scrollTop = parseInt(index) * elementHeight;
        break;
      }
    }
  }
};

onMounted(async () => {
  // We need a key listener
  document.addEventListener("keydown", keyListener);

  const element = listRef.value;

  // Positioning the element
  if (element) {
    // Getting the position of the list box
    const { top } = element.getBoundingClientRect();
    // Getting the window height
    const { innerHeight } = window;
    // How much spaces we have in the bottom
    const bottomDiff = innerHeight - top;

    // If there is not space in bottom but there is space on top
    if (bottomDiff < LIST_HEIGHT + INPUT_HEIGHT && top > LIST_HEIGHT) {
      // We open the list through the top
      positionClass.value = "bottom-11 mb-5";
    } else {
      positionClass.value = "";
    }
  }

  setScrollPosition(modelValue.value);
});

watch(
  () => searchText.value,
  () => {
    setScrollPosition(searchText.value);
  },
);

onUnmounted(() => {
  // We should remove the key listener.
  document.removeEventListener("keydown", keyListener);
});
</script>

<template>
  <div
    ref="listRef"
    :class="[
      'fixed bg-neutral0 rounded-lg border border-neutral300 p-2 overflow-auto z-10 custom-shadow',
      positionClass,
    ]"
    :style="customStyle"
  >
    <div ref="slotRef">
      <slot></slot>
    </div>
  </div>
</template>

<style lang="scss">
.custom-shadow {
  box-shadow: 0rem 0rem 0.875rem 0rem rgba(0, 0, 0, 0.15);
}
</style>
